/**
 * Browser-side sealed-bid envelope encryption for M11 e-GP — a REAL,
 * working implementation of the exact wire protocol the backend's reference
 * bidder client documents at packages/modules/m11-egp/src/service/crypto.ts
 * (`sealBidPayload` there; server-side unseal is `unsealBidPayload`, invoked
 * only once a 2-of-3 evaluator quorum reconstructs the tender's private
 * key). This is genuinely the "bidder's own tooling" that module's own
 * comment says runs outside the backend — never a placeholder "click to
 * seal" button.
 *
 * Protocol (must match the Node implementation byte-for-byte):
 *  1. Generate a random 256-bit AES key (the DEK) and a random 96-bit IV.
 *  2. AES-256-GCM-encrypt the JSON bid payload with the DEK/IV. WebCrypto
 *     returns ciphertext with the 128-bit auth tag appended; the server's
 *     Node `createDecipheriv`/`setAuthTag` expects them SEPARATE, so this
 *     splits the last 16 bytes off before encoding.
 *  3. RSA-OAEP(SHA-256)-encrypt the 32 raw DEK bytes under the tender's
 *     public seal key (fetched via GET /tenders/:id/seal-public-key) — this
 *     is what `wrappedKey` carries. Node's `publicEncrypt({ oaepHash:
 *     'sha256' }, dek)` and WebCrypto's `RSA-OAEP` with `hash: 'SHA-256'`
 *     both use SHA-256 for the MGF1 mask function too (the modern default
 *     both runtimes converged on), so the two interoperate.
 *  4. `payloadSha256` is NOT sent — the server recomputes it itself from the
 *     ciphertext it receives (see routes/bids.ts).
 */

export interface SealedBidEnvelope {
  encryptedPayload: string; // base64
  payloadIv: string; // hex
  payloadAuthTag: string; // hex
  wrappedKey: string; // base64
}

function bufToBase64(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let binary = '';
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary);
}

function bufToHex(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Strips PEM armour/newlines and base64-decodes to the raw SPKI DER the SubtleCrypto API wants. */
function pemToDer(pem: string): ArrayBuffer {
  const stripped = pem
    .replace(/-----BEGIN [^-]+-----/, '')
    .replace(/-----END [^-]+-----/, '')
    .replace(/\s+/g, '');
  const binary = atob(stripped);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

async function importSealPublicKey(publicKeyPem: string): Promise<CryptoKey> {
  return crypto.subtle.importKey('spki', pemToDer(publicKeyPem), { name: 'RSA-OAEP', hash: 'SHA-256' }, false, ['encrypt']);
}

/**
 * Seals `payload` (must include a numeric `priceScr` — the tender's award
 * amount is derived from it at unseal time, see shared.ts's
 * `extractPriceScr`) for submission via POST /tenders/:id/bids.
 */
export async function sealBidPayload(publicKeyPem: string, payload: unknown): Promise<SealedBidEnvelope> {
  const publicKey = await importSealPublicKey(publicKeyPem);

  const dek = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt']);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new TextEncoder().encode(JSON.stringify(payload));

  const combined = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv, tagLength: 128 }, dek, plaintext),
  );
  // WebCrypto appends the 16-byte GCM tag to the ciphertext; Node's
  // createCipheriv/getAuthTag keeps them separate — split to match.
  const ciphertext = combined.slice(0, combined.length - 16);
  const authTag = combined.slice(combined.length - 16);

  const rawDek = new Uint8Array(await crypto.subtle.exportKey('raw', dek));
  const wrappedKey = new Uint8Array(await crypto.subtle.encrypt({ name: 'RSA-OAEP' }, publicKey, rawDek));

  return {
    encryptedPayload: bufToBase64(ciphertext),
    payloadIv: bufToHex(iv),
    payloadAuthTag: bufToHex(authTag),
    wrappedKey: bufToBase64(wrappedKey),
  };
}
